import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { useCreateClient } from '../hooks/useCreateClient';
import { useUpdateClient } from '../hooks/useUpdateClient';
import { useClientContacts } from '@/features/client-contacts/public';
import type { ClientContact, CreateClientContactPayload } from '@/features/client-contacts/public';
import { useToast } from '@/shared/hooks/useToast';
import { createClientSchema, type ClientFormValues } from '../schemas';
import type { Client } from '../types';
import { ClientContactsInlineTable } from './ClientContactsInlineTable';

interface ClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client;
}

export function ClientDialog({ open, onOpenChange, client }: ClientDialogProps) {
  const { t } = useTranslation('clients');
  const schema = createClientSchema(t);
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const { toast } = useToast();

  const [localContacts, setLocalContacts] = useState<ClientContact[]>([]);

  const isEditing = !!client;
  const isPending = createClient.isPending || updateClient.isPending;

  const contactsQuery = useClientContacts(client?.id ?? '', {
    enabled: isEditing && open,
  });

  const contacts = (() => {
    if (isEditing && contactsQuery.data) {
      return contactsQuery.data;
    }
    return localContacts;
  })();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: client?.name ?? '',
      taxId: client?.taxId ?? '',
    },
  });

  const onSubmit = async (data: ClientFormValues) => {
    if (!isEditing && contacts.length === 0) {
      toast.error({ title: t('validation.minOneContact') });
      return;
    }
    try {
      if (isEditing) {
        await updateClient.mutateAsync({
          clientId: client.id,
          payload: { name: data.name, taxId: data.taxId },
        });
        toast.success({ title: t('toast.updated') });
      } else {
        const contactsPayload: CreateClientContactPayload[] = contacts.map((c, index) => ({
          name: c.name,
          email: c.email || undefined,
          phone: c.phone || undefined,
          isPrimary: index === 0,
        }));
        await createClient.mutateAsync({
          name: data.name,
          taxId: data.taxId,
          contacts: contactsPayload,
        });
        toast.success({ title: t('toast.created') });
      }
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setLocalContacts([]);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('dialog.editTitle') : t('dialog.createTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        <form
          id="client-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5 py-2"
        >
          <FormFieldInput
            control={control}
            name="name"
            label={t('dialog.name')}
            floatingLabel
            data-testid="clients-name-input"
          />
          <FormFieldInput
            control={control}
            name="taxId"
            label={t('dialog.taxId')}
            floatingLabel
            data-testid="clients-taxid-input"
          />

          <ClientContactsInlineTable
            contacts={contacts}
            onContactsChange={isEditing ? () => {} : setLocalContacts}
            clientId={client?.id}
            isLoading={contactsQuery.isLoading}
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            data-testid="clients-dialog-cancel"
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            type="submit"
            form="client-form"
            disabled={isPending || (isEditing && !isDirty)}
            data-testid="clients-dialog-submit"
          >
            {isPending ? t('dialog.submitting') : t('dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
